// Clay signals in words a strategist reads. Keep in step with jobs/signal_copy.py;
// tests/test_signal_copy.py fails the build if they drift (GRO-188).

export const LOOKBACK_DAYS = 7;
export const DATE_UNKNOWN = 'date unknown';

const HIDDEN = new Set(['high prio', 'high_prio', 'high-prio']);

// Door sources written as the signal type when a colleague asked for the account (Scout chat or
// Slack, the extension, an upload, reconcile, the Q4 list load). Our own action, never a why-now (GRO-307).
const INTERNAL = new Set(['chat', 'csv', 'extension', 'reconcile', 'q4_list']);

const PHRASES: Record<string, string> = {
  'web intent': 'New website visitor',
  'job posting': 'Hiring for a voice role',
  lookalike: 'Looks like your customers',
  chat: 'Added by a colleague in Scout',
  csv: 'Added from an uploaded list',
  q4_list: 'On the Q4 target list',
  // The four types the nightly Exa sweep sends (jobs/signal_sweep.py).
  funding: 'Raised a funding round',
  'voice product launch': 'Launched a voice product',
  'voice or support hiring': 'Hiring for voice or support',
  'leadership change': 'New leadership',
  // The playground door (jobs/playground_door.py, GRO-359).
  'playground signup': 'Signed up for the playground',
  // Clay's "Scout Signal - Other Voice Provider" workflow.
  'other voice provider': 'Uses another voice provider',
  // Clay's "Scout Signal - Voice Product" and "... - Voice Platform" workflows (GRO-332).
  'voice product': 'Builds a voice product',
  'voice platform': 'Runs voice on a platform',
};

// English %b, same tuple as jobs/signal_copy.py. `en-GB` short September is
// "Sept"; the brief prints "Sep".
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function normalisedSignalName(name: string | undefined | null): string {
  return (name || '').trim().toLowerCase();
}

export function isHiddenSignal(name: string | undefined | null): boolean {
  return HIDDEN.has(normalisedSignalName(name));
}

export function isInternalSignal(name: string | undefined | null): boolean {
  return INTERNAL.has(normalisedSignalName(name));
}

export function signalPhrase(name: string | undefined | null): string {
  const key = normalisedSignalName(name);
  if (!key) return 'Clay signal';
  return PHRASES[key] || (name || '').trim();
}

export function formatSeenDay(raw: string | undefined | null): string {
  if (!raw) return DATE_UNKNOWN;
  const stamp = String(raw).trim().slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(stamp);
  if (!match) return DATE_UNKNOWN;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const seen = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(seen.getTime()) || month < 1 || month > 12) return DATE_UNKNOWN;
  return `${day} ${MONTHS[month - 1]}`;
}

export function signalLine(
  name: string | undefined | null,
  seenRaw: string | undefined | null,
): string | null {
  if (isHiddenSignal(name)) return null;
  return `${signalPhrase(name)} · ${formatSeenDay(seenRaw)}`;
}

export function freshnessStamp(signal: {
  first_seen?: string;
  last_seen?: string;
}): string | undefined {
  return signal.first_seen || signal.last_seen || undefined;
}

// last_seen, then first_seen: "still arriving", the ranker's freshness (rank_books, GRO-259).
export function activityStamp(signal: {
  first_seen?: string;
  last_seen?: string;
}): string | undefined {
  return signal.last_seen || signal.first_seen || undefined;
}

export function claySignalIsFresh(
  signal: { name?: string; first_seen?: string; last_seen?: string } | undefined,
  today: string,
): boolean {
  if (!signal?.name || isHiddenSignal(signal.name)) return false;
  const stamp = activityStamp(signal);
  if (!stamp) return false;
  const seenDay = String(stamp).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(seenDay) || !/^\d{4}-\d{2}-\d{2}$/.test(today)) {
    return false;
  }
  const seen = Date.parse(seenDay + 'T00:00:00Z');
  const now = Date.parse(today + 'T00:00:00Z');
  if (Number.isNaN(seen) || Number.isNaN(now)) return false;
  const ageDays = Math.round((now - seen) / 864e5);
  if (ageDays < 0) return true;
  return ageDays <= LOOKBACK_DAYS;
}
